import type { JackpotCampaign } from "@/campaigns/modules/jackpot/types";
import { jackpotCardState } from "@/campaigns/modules/jackpot/types";
import { cn } from "@/lib/utils";

import { JackpotCard } from "./jackpot-card";
import type { JackpotCardSize } from "./jackpot-pot";
import { useJackpotClock } from "./use-jackpot-clock";

/**
 * The main column of a jackpot campaign: one card per jackpot in the operator's order, nothing
 * above them — the "4 jackpots · Live" head row was dropped (the count is on the home card, and
 * each card carries its own state). This is the component that
 * owns the board's clock — `useJackpotClock` is a hook, so it cannot live in the module's plain
 * `detailView` function — and hands `now` down, so every card ticks from the same instant.
 *
 * Two card sizes, chosen by count: `large` up to two jackpots, `compact` at three or four. The
 * cards hug their content; whatever column height is left stays empty, by decision.
 */
export const JackpotBoard = ({
  jackpotCampaign,
  layout = "stack",
  className,
}: {
  jackpotCampaign: JackpotCampaign;
  /** `grid`: phones held sideways — the pots side by side, filling the pane's height: one, two
   *  side by side, three as one wide + two below, four as 2×2. A pot that must be won now moves to the front there: with
   *  the pots in one glance, the one worth acting on reads first. */
  layout?: "stack" | "grid";
  className?: string;
}) => {
  const now = useJackpotClock(jackpotCampaign);
  const { campaign, jackpots, recent_wins } = jackpotCampaign;
  const grid = layout === "grid";
  const size: JackpotCardSize =
    jackpots.length <= (grid ? 1 : 2) ? "large" : "compact";
  const ordered = grid
    ? [...jackpots].sort(
        (a, b) =>
          Number(jackpotCardState(b, campaign, now) === "mustWin") -
          Number(jackpotCardState(a, campaign, now) === "mustWin")
      )
    : jackpots;

  return (
    <div
      className={cn(
        grid
          ? cn(
              // Two columns at most: beside the wins column a third column leaves ~130px a pot,
              // too narrow for the amount. Three pots: the first (the one that must be won, if
              // any) spans the top row.
              "grid h-full w-full gap-2",
              jackpots.length >= 2 && "grid-cols-2",
              jackpots.length >= 3 && "grid-rows-2",
              jackpots.length === 3 && "[&>*:first-child]:col-span-2"
            )
          : "flex w-full flex-col gap-3 lg:gap-4",
        className
      )}
    >
      {ordered.map((view) => (
        <JackpotCard
          key={view.jackpot.id}
          view={view}
          campaign={campaign}
          size={size}
          now={now}
          dense={grid}
          className={grid ? "h-full" : undefined}
          latestWin={
            recent_wins.find((row) => row.jackpotId === view.jackpot.id) ?? null
          }
        />
      ))}
    </div>
  );
};
